import { stripTags } from '@/content/story';
import { fetchCached } from '@/speech/engine/fetchCached';
import { SheetIndex, type Segment, type SheetManifest } from './sheets';

/**
 * The app's spoken output: feedback lines, word models and playback of the child's own attempt,
 * played in order as one "turn". Behind an interface so native apps can swap in their own
 * text-to-speech later; the web implementation uses the Web Speech API and Web Audio.
 */

export type VoicePart =
  /** A spoken line. */
  | { kind: 'say'; text: string }
  /** A clear model of a word or syllable, spoken a little slower. */
  | { kind: 'model'; text: string }
  /** The child's own recording (16 kHz mono). */
  | { kind: 'playback'; audio: Float32Array }
  | { kind: 'pause'; ms: number };

export interface Voice {
  /** Plays the parts in order; resolves when done or interrupted. Interrupts any current turn. */
  play(parts: readonly VoicePart[], hooks?: VoiceHooks): Promise<'done' | 'interrupted'>;
  /** Stops talking now (barge-in, mic off, leaving the screen). */
  stop(): void;
  readonly speaking: boolean;
}

export interface VoiceHooks {
  /** A playback of the child's recording starts or ends (the listener mutes meanwhile). */
  onPlayback?: (playing: boolean) => void;
  /** A part starts (for captions that follow the voice), with its length when known (s). */
  onPart?: (part: VoicePart, seconds?: number) => void;
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/** Picks a clear US English voice; prefers natural-sounding ones where the browser has them. */
function chooseVoice(): SpeechSynthesisVoice | undefined {
  const voices = window.speechSynthesis.getVoices().filter((v) => /^en[-_]US/i.test(v.lang));
  const prefer = [/natural/i, /aria|jenny|ana|samantha/i, /google us english/i, /female/i];
  for (const pattern of prefer) {
    const voice = voices.find((v) => pattern.test(v.name));
    if (voice) return voice;
  }
  return voices.find((v) => v.localService) ?? voices[0];
}

/** Edges of a played-back recording: faded, so a cut edge does not click (QA 2026-10-11: "a beep at
 * the end"), and the last few milliseconds dropped (they can hold a burst from the cut). */
const PLAYBACK_FADE_IN_S = 0.015;
const PLAYBACK_FADE_OUT_S = 0.06;
const PLAYBACK_TRIM_S = 0.04;
/** Peak the playback is scaled down to when louder (a near-clipping attempt crackles). */
const PLAYBACK_PEAK = 0.8;

/**
 * The child's recording ready to play back (16 kHz): the last PLAYBACK_TRIM_S dropped, faded in
 * and out, and scaled down to PLAYBACK_PEAK when louder.
 */
export function playbackSamples(samples: Float32Array, rate = 16000): Float32Array {
  const out = samples.slice(0, Math.max(0, samples.length - Math.round(PLAYBACK_TRIM_S * rate)));
  let peak = 0;
  for (const v of out) peak = Math.max(peak, Math.abs(v));
  const scale = peak > PLAYBACK_PEAK ? PLAYBACK_PEAK / peak : 1;
  const fadeIn = Math.min(out.length, Math.round(PLAYBACK_FADE_IN_S * rate));
  const fadeOut = Math.min(out.length, Math.round(PLAYBACK_FADE_OUT_S * rate));
  for (let i = 0; i < out.length; i++) {
    let g = scale;
    if (i < fadeIn) g *= i / fadeIn;
    const fromEnd = out.length - 1 - i;
    if (fromEnd < fadeOut) g *= fromEnd / fadeOut;
    out[i] = (out[i] ?? 0) * g;
  }
  return out;
}

/** Plays the child's recordings (16 kHz mono) through Web Audio, one at a time. */
class Playback {
  private source: AudioBufferSourceNode | undefined;
  /** Bumped by stop(), so a playback still waiting to start does not start afterwards. */
  private serial = 0;
  private readonly getContext: () => AudioContext;

  constructor(getContext: () => AudioContext) {
    this.getContext = getContext;
  }

  async play(samples: Float32Array): Promise<void> {
    const serial = this.serial;
    const context = this.getContext();
    if (context.state === 'suspended') await context.resume();
    if (serial !== this.serial) return;
    const audio = playbackSamples(samples);
    if (audio.length === 0) return;
    const buffer = context.createBuffer(1, audio.length, 16000);
    buffer.copyToChannel(new Float32Array(audio), 0);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    this.source = source;
    await new Promise<void>((resolve) => {
      source.onended = () => {
        resolve();
      };
      source.start();
    });
  }

  stop(): void {
    this.serial++;
    try {
      this.source?.stop();
    } catch {
      // already stopped
    }
    this.source = undefined;
  }
}

export class WebVoice implements Voice {
  private turn = 0;
  private active = false;
  private utterance: SpeechSynthesisUtterance | undefined;
  private readonly playback: Playback;

  /** `getContext` supplies the AudioContext used for playback (shared with listening). */
  constructor(getContext: () => AudioContext) {
    this.playback = new Playback(getContext);
  }

  get speaking(): boolean {
    return this.active;
  }

  async play(parts: readonly VoicePart[], hooks: VoiceHooks = {}): Promise<'done' | 'interrupted'> {
    this.stop();
    const turn = ++this.turn;
    this.active = true;
    try {
      for (const part of parts) {
        if (turn !== this.turn) return 'interrupted';
        hooks.onPart?.(part);
        if (part.kind === 'pause') await sleep(part.ms);
        else if (part.kind === 'playback') {
          hooks.onPlayback?.(true);
          try {
            await this.playback.play(part.audio);
          } finally {
            hooks.onPlayback?.(false);
          }
        } else await this.utter(part.text, part.kind === 'model' ? 0.75 : 0.95);
      }
      return turn === this.turn ? 'done' : 'interrupted';
    } finally {
      if (turn === this.turn) this.active = false;
    }
  }

  stop(): void {
    this.turn++;
    this.active = false;
    // Only cancel when something is queued: on some systems cancel() blocks for seconds.
    const synth = 'speechSynthesis' in window ? window.speechSynthesis : undefined;
    if (synth && (synth.speaking || synth.pending)) synth.cancel();
    this.playback.stop();
  }

  private async utter(text: string, rate: number): Promise<void> {
    if (!('speechSynthesis' in window)) return sleep(250);
    // Audio tags ("[mysterious]") are for the recorded voice only.
    const utterance = new SpeechSynthesisUtterance(stripTags(text));
    const voice = chooseVoice();
    if (voice) utterance.voice = voice;
    utterance.lang = 'en-US';
    utterance.rate = rate;
    utterance.pitch = 1.05;
    // Keep a reference until it ends: Chrome can drop the events of collected utterances.
    this.utterance = utterance;
    const ended = new Promise<void>((resolve) => {
      utterance.onend = () => {
        resolve();
      };
      utterance.onerror = () => {
        resolve();
      };
    });
    window.speechSynthesis.speak(utterance);
    // Some platforms never fire `end` (no voices, no audio device, or Chrome losing the
    // utterance): give up after a generous estimate of the speaking time.
    const estimate = 800 + (text.split(/\s+/).length * 450) / rate;
    await Promise.race([ended, sleep(estimate * 1.5)]);
    if (this.utterance === utterance) this.utterance = undefined;
  }
}

/** Crossfade between spliced ranges of recorded speech. */
const FADE_S = 0.01;
/** Fade at the start and end of a played line (short, so no speech is lost). */
const EDGE_FADE_S = 0.004;

/** Decoded recorded-voice sheets (see sheets.ts), loaded once from a manifest. */
export class SheetLibrary {
  readonly index: SheetIndex;
  readonly buffers: AudioBuffer[];

  private constructor(index: SheetIndex, buffers: AudioBuffer[]) {
    this.index = index;
    this.buffers = buffers;
  }

  /**
   * Loads the manifest and its sheets (each cached after the first download; sheet files have
   * content-hashed names). Resolves to undefined when there is no manifest or a sheet fails, so the
   * browser voice carries on.
   */
  static async load(
    manifestUrl: string,
    getContext: () => AudioContext,
  ): Promise<SheetLibrary | undefined> {
    try {
      // Inside the try: without Web Audio (some test browsers) the browser voice carries on.
      const context = getContext();
      const response = await fetch(manifestUrl, { cache: 'no-cache' });
      if (!response.ok) return undefined;
      const manifest = (await response.json()) as SheetManifest;
      const base = new URL(manifestUrl, window.location.href);
      const buffers = await Promise.all(
        manifest.sheets.map(async (sheet) =>
          context.decodeAudioData(await fetchCached(new URL(sheet.file, base).href)),
        ),
      );
      return new SheetLibrary(new SheetIndex(manifest), buffers);
    } catch {
      return undefined;
    }
  }
}

/**
 * The recorded voice: lines and model words found in the sheets play from the recordings, spliced
 * on the device (a line around its word); anything else falls back to the browser's voice.
 */
export class SheetVoice implements Voice {
  private turn = 0;
  private active = false;
  private library: SheetLibrary | undefined;
  private sources: AudioBufferSourceNode[] = [];
  /** Ends the line being waited for: a suspended context (the app in the background) never fires
   * `ended`, so stop() releases the wait itself. */
  private release: (() => void) | undefined;
  private readonly getContext: () => AudioContext;
  private readonly fallback: Voice;

  constructor(
    getContext: () => AudioContext,
    fallback: Voice,
    library: Promise<SheetLibrary | undefined>,
  ) {
    this.getContext = getContext;
    this.fallback = fallback;
    void library.then((l) => {
      this.library = l;
    });
  }

  get speaking(): boolean {
    return this.active || this.fallback.speaking;
  }

  async play(parts: readonly VoicePart[], hooks: VoiceHooks = {}): Promise<'done' | 'interrupted'> {
    this.stop();
    // One voice per turn: if any line of it is not recorded, the whole turn uses the browser voice
    // (switching voices mid-reply sounded like two people talking, 2026-10-11 QA session).
    const library = this.library;
    const spoken = parts.filter((p) => p.kind === 'say' || p.kind === 'model');
    if (!library || spoken.some((p) => !library.index.find(p.text))) {
      return this.fallback.play(parts, hooks);
    }
    const turn = ++this.turn;
    this.active = true;
    try {
      for (const part of parts) {
        if (turn !== this.turn) return 'interrupted';
        const segments =
          part.kind === 'say' || part.kind === 'model' ? library.index.find(part.text) : undefined;
        if (segments) {
          hooks.onPart?.(
            part,
            segments.reduce((sum, { range }) => sum + Math.max(0, range.end - range.start), 0),
          );
          await this.playSegments(segments, library);
        } else {
          // One part through the browser voice (playback and pauses included).
          const outcome = await this.fallback.play([part], hooks);
          if (outcome === 'interrupted') return 'interrupted';
        }
      }
      return turn === this.turn ? 'done' : 'interrupted';
    } finally {
      if (turn === this.turn) this.active = false;
    }
  }

  stop(): void {
    this.turn++;
    this.active = false;
    this.fallback.stop();
    this.release?.();
    this.release = undefined;
    for (const source of this.sources) {
      try {
        source.stop();
      } catch {
        // not started or already stopped
      }
    }
    this.sources = [];
  }

  /** Schedules the segments back to back on the audio clock, so a spliced line has no gaps. */
  private async playSegments(segments: Segment[], library: SheetLibrary): Promise<void> {
    const context = this.getContext();
    if (context.state === 'suspended') await context.resume();
    let at = context.currentTime + 0.02;
    const sources = segments.flatMap(({ sheet, range }, i) => {
      const buffer = library.buffers[sheet];
      if (!buffer) return [];
      const source = context.createBufferSource();
      source.buffer = buffer;
      // Each range fades in and out over FADE_S and overlaps its neighbour by as much, so a join
      // inside running speech (a word spliced after "say") is a crossfade, not a click.
      const gain = context.createGain();
      source.connect(gain).connect(context.destination);
      const duration = Math.max(0, range.end - range.start);
      const fade = Math.min(FADE_S, duration / 4);
      const start = i === 0 ? at : at - fade;
      // Every range fades in and out, the line's outer edges too (a short EDGE_FADE_S): a word cut
      // tight from its recording starts or ends mid-sound, and a hard edge there clicks (the
      // crackle in the practice, which splices words; QA 2026-10-11).
      const fadeIn = i === 0 ? Math.min(EDGE_FADE_S, duration / 4) : fade;
      const fadeOut = i === segments.length - 1 ? Math.min(EDGE_FADE_S, duration / 4) : fade;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(1, start + fadeIn);
      gain.gain.setValueAtTime(1, start + duration - fadeOut);
      gain.gain.linearRampToValueAtTime(0, start + duration);
      source.start(start, range.start, duration);
      at = start + duration;
      return [source];
    });
    this.sources = sources;
    const last = sources.at(-1);
    if (!last) return;
    await new Promise<void>((resolve) => {
      this.release = resolve;
      last.onended = () => {
        resolve();
      };
    });
  }
}

/** One line or sound the simulated voice "said", for tests and development logs. */
export interface SpokenRecord {
  kind: VoicePart['kind'];
  text?: string;
  /** Length of a playback, in seconds. */
  seconds?: number;
  interrupted?: boolean;
}

/**
 * Stands in for text-to-speech where no speaker exists (development without audio output, tests):
 * each line takes about as long as speaking it would, the child's recording still plays through
 * Web Audio, and everything said is recorded in `spoken`.
 */
export class SimulatedVoice implements Voice {
  readonly spoken: SpokenRecord[] = [];
  private turn = 0;
  private active = false;
  private readonly playback: Playback;
  private readonly msPerWord: number;

  private readonly onSpeak: ((ms: number) => () => void) | undefined;
  private stopEcho: (() => void) | undefined;

  /**
   * `onSpeak` is told each time the voice "speaks" a line and for how long (echo simulation); it
   * returns a function that ends that sound early, called when the voice is stopped.
   */
  constructor(
    getContext: () => AudioContext,
    msPerWord = 380,
    onSpeak?: (ms: number) => () => void,
  ) {
    this.playback = new Playback(getContext);
    this.onSpeak = onSpeak;
    this.msPerWord = msPerWord;
  }

  get speaking(): boolean {
    return this.active;
  }

  async play(parts: readonly VoicePart[], hooks: VoiceHooks = {}): Promise<'done' | 'interrupted'> {
    this.stop();
    const turn = ++this.turn;
    this.active = true;
    try {
      for (const part of parts) {
        if (turn !== this.turn) return 'interrupted';
        hooks.onPart?.(part);
        if (part.kind === 'playback') {
          this.spoken.push({ kind: 'playback', seconds: part.audio.length / 16000 });
          hooks.onPlayback?.(true);
          try {
            await this.playback.play(part.audio);
          } finally {
            hooks.onPlayback?.(false);
          }
        } else if (part.kind === 'pause') {
          await this.wait(part.ms);
        } else {
          this.spoken.push({ kind: part.kind, text: part.text });
          const words = part.text.split(/\s+/).length;
          const ms = 250 + words * this.msPerWord * (part.kind === 'model' ? 1.3 : 1);
          this.stopEcho = this.onSpeak?.(ms);
          await this.wait(ms);
        }
      }
      return turn === this.turn ? 'done' : 'interrupted';
    } finally {
      if (turn === this.turn) this.active = false;
    }
  }

  stop(): void {
    if (this.active) {
      const last = this.spoken.at(-1);
      if (last) last.interrupted = true;
    }
    this.turn++;
    this.active = false;
    this.playback.stop();
    this.stopEcho?.();
    this.stopEcho = undefined;
    this.wake?.();
  }

  private wake: (() => void) | undefined;

  /** Sleeps for `ms`, or until stop(), like real speech that ends when cancelled. */
  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        if (this.wake === done) this.wake = undefined;
        resolve();
      };
      const timer = setTimeout(done, ms);
      this.wake = done;
    });
  }
}
